import type {ReactNode} from 'react'
import {forwardRef} from 'react'
import classNames from 'classnames'
import type {TabsValue} from './context'
import {useTabs} from './context'
import useCallbackRef from '../hooks/useCallbackRef'
import type {CommonProps} from '../@types/common'

export interface TabNavProps extends CommonProps {
  disabled?: boolean
  icon?: string | ReactNode
  value: TabsValue
}

const TabNav = forwardRef<HTMLDivElement, TabNavProps>((props, ref) => {
  const {
    value: valueProp,
    disabled,
    className,
    icon,
    children,
    ...rest
  } = props

  const {value, onValueChange, variant} = useTabs()
  const isSelected = valueProp === value


  const onTabNavClick = useCallbackRef(() => {
    if (!isSelected && !disabled) {
      onValueChange?.(valueProp)
    }
  })


  const tabNavClass = classNames(
      'tab-nav',
      `tab-nav-${variant}`,
      'relative px-4 py-3 rounded-lg font-medium text-sm transition-all duration-200 cursor-pointer select-none',
      'border border-transparent',
      isSelected && [
        'bg-gradient-to-r from-blue-50 to-indigo-50',
        'border-blue-200',
        'text-blue-700',
        'shadow-sm',
        'transform scale-105'
      ],
      !isSelected && [
        'text-slate-600',
        'hover:bg-slate-50',
        'hover:text-slate-800',
        'hover:border-slate-200',
        'hover:shadow-sm'
      ],
      disabled && [
        'opacity-50',
        'cursor-not-allowed',
        'hover:bg-transparent',
        'hover:text-slate-600',
        'hover:border-transparent'
      ],
      className
  )

  return (
      <div
          ref={ref}
          className={tabNavClass}
          role="tab"
          aria-selected={isSelected}
          tabIndex={0}
          onClick={onTabNavClick}
          onKeyDown={onTabNavClick}
          {...rest}
      >
        {icon && (
            <div className="tab-nav-icon inline-flex items-center justify-center w-5 h-5 mr-2">
              {icon}
            </div>
        )}
        <span className="inline-flex items-center gap-2">
        {children}
          {isSelected && (
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
          )}
      </span>
      </div>
  )
})

TabNav.displayName = 'TabNav'

export default TabNav